import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {DEMO_TOPICS, DEMO_SUBTOPIC_INDEX} from './_demoConfig'

export interface DemoPanelVideo {
  /** Subtopic i18n key — used as a stable id and to look up the parent topic. */
  key: string
  title: string
  url: string
  duration: string
  topicKey: string
}

interface DemoPanelProps {
  totalDuration: string
  videosCount: number

  // Player mode
  videos?: DemoPanelVideo[]
  currentVideoIndex?: number
  isPlaying?: boolean
  isPanelMinimized?: boolean
  onVideoSelect?: (index: number) => void
  onPlayFirstVideo?: () => void
  onCollapsePanel?: () => void

  // Topics mode
  selectedSubtopicKeys?: string[]
  videoDurations?: Record<string, string>

  mode: 'player' | 'topics'
}

/**
 * Parse "X min Y sec" / "Y sec" → total seconds. Returns 0 on miss.
 * Used to compute the per-topic group duration in the right-side panel.
 */
const parseDurationSeconds = (duration: string | undefined): number => {
  if (!duration) return 0
  const match = duration.match(/(?:(\d+)\s*min)?\s*(\d+)\s*sec/)
  if (!match) return 0
  return parseInt(match[1] || '0', 10) * 60 + parseInt(match[2], 10)
}

const formatSecondsAsDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  if (minutes > 0) return `${minutes} min ${remainingSeconds} sec`
  return `${remainingSeconds} sec`
}

const DemoPanel: React.FC<DemoPanelProps> = ({
  totalDuration,
  videosCount,
  videos = [],
  currentVideoIndex = -1,
  isPanelMinimized = false,
  onVideoSelect,
  onPlayFirstVideo,
  onCollapsePanel,
  selectedSubtopicKeys = [],
  videoDurations = {},
  mode,
}) => {
  const intl = useIntl()

  /**
   * Group selected items by their parent topic, preserving config order.
   * `topicKey` → array of `{key, label, duration}` rows.
   * Computed differently per mode but produces the same shape so the JSX is shared.
   */
  const grouped = useMemo(() => {
    const result: Array<{
      topicKey: string
      topicLabel: string
      topicDuration: string
      items: Array<{
        key: string
        label: string
        duration: string
        index: number
      }>
    }> = []

    // Build a flat list with stable indices for click handling.
    type Row = {key: string; topicKey: string; duration: string; index: number}
    let rows: Row[] = []

    if (mode === 'player') {
      rows = videos.map((v, index) => ({
        key: v.key,
        topicKey: v.topicKey,
        duration: v.duration,
        index,
      }))
    } else {
      rows = selectedSubtopicKeys.map((key, index) => {
        const entry = DEMO_SUBTOPIC_INDEX[key]
        return {
          key,
          topicKey: entry?.topic.key ?? '',
          duration: videoDurations[key] || intl.formatMessage({id: 'DEMO_TOPICS_LOADING'}),
          index,
        }
      })
    }

    // Walk topics in config order to preserve a deterministic display order.
    DEMO_TOPICS.forEach((topic) => {
      const topicRows = rows.filter((r) => r.topicKey === topic.key)
      if (topicRows.length === 0) return
      const topicSeconds = topicRows.reduce((sum, r) => sum + parseDurationSeconds(r.duration), 0)
      result.push({
        topicKey: topic.key,
        topicLabel: intl.formatMessage({id: topic.key}),
        topicDuration: formatSecondsAsDuration(topicSeconds),
        items: topicRows.map((r) => ({
          key: r.key,
          label: intl.formatMessage({id: r.key}),
          duration: r.duration,
          index: r.index,
        })),
      })
    })

    return result
  }, [mode, videos, selectedSubtopicKeys, videoDurations, intl])

  // Player collapsed
  if (mode === 'player' && isPanelMinimized) {
    return (
      <button onClick={onCollapsePanel} className='demo-collapsed-panel'>
        <img
          src='/media/gori/demo/collapse.svg'
          alt='Expand'
          width='16'
          height='16'
          className='demo-collapsed-panel-icon'
        />
      </button>
    )
  }

  const summarySuffix =
    mode === 'player'
      ? intl.formatMessage({id: 'DEMO_PANEL_SUMMARY_TOPICS'})
      : intl.formatMessage({id: 'DEMO_PANEL_SUMMARY_SUBTOPICS'})

  const hasContent = grouped.length > 0

  return (
    <div
      className={
        mode === 'player'
          ? 'demo-panel-player d-flex flex-column'
          : // `demo-topics-panel-root` adds `min-height: 0` — required so the
            // wrapper can flex-shrink below its content's intrinsic 1500px+
            // height; without it, `.demo-panel-scroll` inside cannot trigger
            // overflow-y: auto, and the sibling "Start My Demo" button gets
            // pushed below the parent's 100vh viewport.
            'd-flex flex-column flex-grow-1 demo-topics-panel-root'
      }
    >
      <div className='demo-panel-header d-flex align-items-center'>
        {mode === 'player' && (
          <button onClick={onCollapsePanel} className='btn p-0 demo-collapse-btn'>
            <img src='/media/gori/demo/collapse.svg' alt='Collapse' width='16' height='16' />
          </button>
        )}
        <div className='flex-grow-1 text-center'>
          <h3 className='demo-panel-title'>{intl.formatMessage({id: 'DEMO_PLAYER_MY_DEMO'})}</h3>
          <div className='demo-panel-summary'>
            {videosCount} {summarySuffix} | {totalDuration}
          </div>
        </div>
      </div>

      <div className='flex-grow-1 demo-panel-scroll'>
        {hasContent ? (
          grouped.map((group, groupIdx) => {
            // In player mode, "active" highlights the topic that owns the playing video.
            const isActiveTopic =
              mode === 'player' &&
              currentVideoIndex >= 0 &&
              group.items.some((item) => item.index === currentVideoIndex)

            return (
              <div className='demo-panel-topic-group' key={group.topicKey}>
                <div
                  className={`demo-main-topic d-flex align-items-center justify-content-between ${
                    isActiveTopic ? 'active' : ''
                  }`}
                  onClick={mode === 'player' && groupIdx === 0 ? onPlayFirstVideo : undefined}
                >
                  <div className='demo-main-topic-content'>
                    <div className='demo-main-topic-title'>{group.topicLabel}</div>
                    <div className='demo-main-topic-duration'>{group.topicDuration}</div>
                  </div>
                  {mode === 'player' && (
                    <div className='demo-play-button'>
                      <img
                        src={
                          isActiveTopic
                            ? '/media/gori/demo/play_active.svg'
                            : '/media/gori/demo/play_inactive.svg'
                        }
                        alt='Play'
                        width='32'
                        height='32'
                      />
                    </div>
                  )}
                </div>

                <div className='demo-subtopic-arrow-container'>
                  <img
                    src='/media/gori/demo/subtopic-arrow.svg'
                    alt='subtopic arrow'
                    className='demo-subtopic-arrow'
                  />
                </div>

                <div className='demo-panel-subtopics'>
                  {group.items.map((item) => {
                    const isActiveItem = mode === 'player' && item.index === currentVideoIndex
                    return (
                      <div
                        key={item.key}
                        className={`demo-subtopic d-flex align-items-start demo-subtopic-item ${
                          isActiveItem ? 'active' : ''
                        }`}
                        onClick={mode === 'player' ? () => onVideoSelect?.(item.index) : undefined}
                      >
                        <div className='flex-grow-1 demo-subtopic-content'>
                          <div className={`demo-subtopic-title ${isActiveItem ? 'active' : ''}`}>
                            {item.label}
                          </div>
                          <div className='demo-subtopic-duration'>{item.duration}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })
        ) : (
          <div className='demo-placeholder'>
            <img
              src='/media/gori/demo/demo_no_topics_selected_placeholder.webp'
              alt='No topics selected'
              className='demo-placeholder-image'
            />
            <div className='demo-placeholder-text'>
              {intl.formatMessage({id: 'DEMO_TOPICS_PLACEHOLDER_TEXT'})}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DemoPanel
